<script lang="ts" setup>
import { useStoreConfig } from '~/store/storeConfig'

import Logo from '@/assets/images/Logo-white.svg'
import LinkedInIcon from 'assets/icons/social/linkedin.png'
import FacebookIcon from 'assets/icons/social/facebook.png'
import InstagramIcon from 'assets/icons/social/instagram.png'
import type { Region } from '~/types/config/Region'

import { QUERY_PARAMETER_FOR_REGION_PREFERENCE } from '~/composables/constants/regions'
import { useLanguageStore } from '~/store/language'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { Routes } from '~/enums/Routes'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'

const storeConfig = useStoreConfig()

const locale = useState<string>('locale.setting')

const languageStore = useLanguageStore()

const { data } = await useAsyncData('regions', () =>
  $fetch<Region[]>('/api/config/regions', {
    method: 'POST',
    body: {
      languageId: languageStore.getLanguageId,
    },
    headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
    ...fetchRetryParameters,
  }).catch((): Region[] => [])
)

watch(locale, () => {
  refreshNuxtData('regions')
})

function formatRegionLink(region: Region): string {
  const redirectUrl = new URL(region.url)
  redirectUrl.searchParams.set(QUERY_PARAMETER_FOR_REGION_PREFERENCE, '1')

  return redirectUrl.href
}

function isRegionActive(region: Region): boolean {
  return region.region_iso_code === storeConfig.getSite?.region_iso_code
}

const siteLanguage = computed(() => storeConfig.getCurrentSiteLanguage)

const regionName = computed<string>(() => data.value?.find((region) => isRegionActive(region))?.region_name ?? '')
</script>

<template>
  <div v-if="storeConfig.isMobile" class="mobile-footer-dropdown">
    <Accordion v-if="regionName">
      <template #header>
        <div>
          {{ regionName }}
        </div>
      </template>
      <div class="regions-list">
        <a v-for="(item, i) in data" :key="i" :href="formatRegionLink(item)" :class="{ active: isRegionActive(item) }">
          {{ item?.region_name }}
        </a>
      </div>
    </Accordion>

    <hr class="my-4" />
    <div class="socials flex gap-4">
      <template v-if="siteLanguage?.pivot.linkedin_url">
        <a :href="siteLanguage?.pivot.linkedin_url" target="_blank">
          <img class="w-10 h-10" :src="LinkedInIcon" alt="LinkedIn" />
        </a>
      </template>
      <template v-if="siteLanguage?.pivot.instagram_url">
        <a :href="siteLanguage?.pivot.instagram_url" target="_blank">
          <img class="w-10 h-10" :src="InstagramIcon" alt="Instagram" />
        </a>
      </template>
      <template v-if="siteLanguage?.pivot.facebook_url">
        <a :href="siteLanguage?.pivot.facebook_url" target="_blank">
          <img class="w-10 h-10" :src="FacebookIcon" alt="Facebook" />
        </a>
      </template>
    </div>
  </div>

  <div v-else class="footer-nav flex items-center gap-7 w-full flex-1">
    <div class="logo basis-1/4">
      <div class="flex align-start">
        <NuxtLink :to="getLocalizedRoute(Routes.Index)">
          <img :src="Logo" alt="Logo" />
        </NuxtLink>
      </div>
    </div>
    <div class="stores basis-2/4">
      <a v-for="(item, i) in data" :key="i" :href="formatRegionLink(item)" :class="{ active: isRegionActive(item) }">
        {{ item?.region_name }}
      </a>
    </div>
    <div class="socials flex justify-end basis-1/4">
      <template v-if="siteLanguage?.pivot.linkedin_url">
        <a :href="siteLanguage?.pivot.linkedin_url" target="_blank">
          <img class="w-10 h-10" :src="LinkedInIcon" alt="LinkedIn" />
        </a>
      </template>
      <template v-if="siteLanguage?.pivot.instagram_url">
        <a :href="siteLanguage?.pivot.instagram_url" target="_blank">
          <img class="w-10 h-10" :src="InstagramIcon" alt="Instagram" />
        </a>
      </template>
      <template v-if="siteLanguage?.pivot.facebook_url">
        <a :href="siteLanguage?.pivot.facebook_url" target="_blank">
          <img class="w-10 h-10" :src="FacebookIcon" alt="Facebook" />
        </a>
      </template>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.footer-nav {
  .stores {
    a {
      padding: 0 1.5rem;
      color: theme('colors.gray4');
      border-left: theme('vars.borderDark');

      &:first-of-type {
        padding-left: 0;
        border-left: 0;
      }

      &.active {
        color: theme('colors.brand.primary');
        font-weight: 700;
      }
    }
  }

  .socials {
    gap: 1rem;
  }
}

.mobile-footer-dropdown {
  .regions-list {
    display: flex;
    flex-direction: column;
    padding: 0;

    a {
      padding: 1rem;
      color: theme('colors.white');
      @apply dark:text-dmText1;

      &.active {
        display: none;
      }
    }
  }
}
</style>
